// Diary meals (CONTEXT.md: "Diary meal") — one row per user per date holding
// that date's meals, global like body measurements and outside week
// snapshots (docs/adr/0028).

export type MealType = "breakfast" | "lunch" | "dinner" | "supper" | "snack";

export const MEAL_TYPE_CATALOG: readonly { key: MealType; label: string }[] = [
  { key: "breakfast", label: "Frokost" },
  { key: "lunch", label: "Lunsj" },
  { key: "snack", label: "Mellommåltid" },
  { key: "dinner", label: "Middag" },
  { key: "supper", label: "Kveldsmat" },
];

export function mealTypeLabel(type: MealType): string {
  return MEAL_TYPE_CATALOG.find((t) => t.key === type)?.label ?? "";
}

/**
 * The Meal type a time of day suggests (docs/meal-diary-design.md): before
 * 10 Frokost, 10–14 Lunsj, 14–15 Mellommåltid, 15–19 Middag, after Kveldsmat.
 * A suggestion only — one tap picks another.
 */
export function suggestMealType(time: string): MealType {
  const hour = Number(time.slice(0, 2));
  if (hour < 10) return "breakfast";
  if (hour < 14) return "lunch";
  if (hour < 15) return "snack";
  if (hour < 19) return "dinner";
  return "supper";
}

// A product's ingredient as its label prints it, top level only
// (CONTEXT.md: "Label ingredient").
export interface LabelIngredient {
  name: string;
  grams: number; // estimated, finite, ≥ 0
}

export interface DiaryMeal {
  id: string; // UUID
  time: string; // "HH:MM", 24h
  type: MealType;
  // The user's own words — the one description of the meal. Every estimate
  // is made from it as it stands, so the ingredients always belong to it.
  description: string;
  // Present once the AI has estimated the meal; absent on a meal saved from
  // text alone («Ingen ingrediensliste»).
  name?: string;
  ingredients?: LabelIngredient[];
}

export interface DiaryMealsData {
  date: string; // YYYY-MM-DD — duplicated from the id for range reads
  meals: DiaryMeal[]; // stored in any order; always shown by time
}

export const DIARY_MEALS_TYPE = "diary_meals";

// 59 chars ("diary_meals:" + 36-char uuid + ":" + 10-char date), inside the
// RxDB id maxLength of 64.
export function diaryMealsId(userId: string, date: string): string {
  return `diary_meals:${userId}:${date}`;
}

/** Earliest first; meals at the same minute keep their stored order. */
export function mealsByTime(meals: DiaryMeal[]): DiaryMeal[] {
  return [...meals].sort((a, b) => a.time.localeCompare(b.time));
}

/** The wall-clock "HH:MM" of `date`, the default time of a new meal. */
export function clockTimeOf(date: Date): string {
  const hours = String(date.getHours()).padStart(2, "0");
  const minutes = String(date.getMinutes()).padStart(2, "0");
  return `${hours}:${minutes}`;
}
